/**
 * Outil « Produit scalaire » (Boîte à outils, 1re → prépa).
 *
 * Deux vecteurs u et v partent de O ; on déplace leurs extrémités. Le projeté
 * de v sur la droite de u se dessine : u · v = ‖u‖ × OH, positif si H est du
 * côté de u, négatif sinon, nul à angle droit. Trois calculs côte à côte :
 * coordonnées (exact), normes et angle, projection.
 */

import { useRef, useState } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { Note, PRIMARY_BUTTON, ToggleChip } from '../../ui/controls';
import { makeFrame, snapPoint } from '../../ui/frame';
import { exactSqrt, formatNumber } from '../../ui/format';
import {
  angleArcPath,
  DragHandle,
  FIGURE_BG_CLASS,
  HaloText,
  PlotGrid,
  RightAngleMark,
  VectorArrow,
} from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import { texFactor } from '../vector-sum/vectors';
import { angleDeg, dot, GOALS, norm2, projection, sign, type Vec } from './dot';

const SIZE = 320;
const VP = { xMin: -5, xMax: 5, yMin: -5, yMax: 5 };
const frame = makeFrame(VP, SIZE);
const U_COLOR = 'var(--color-primary-500)';
const V_COLOR = 'var(--color-accent-500)';
const SIGN_COLOR = {
  positive: 'var(--color-success-500)',
  negative: 'var(--color-error-500)',
  zero: 'var(--color-ink-500)',
} as const;

type Tip = 'u' | 'v';

const tex = (v: number, d = 2) => formatNumber(v, d).replace(',', '{,}').replace('−', '-');

export function DotProductTool({ onSend, sendCost }: ToolProps) {
  const [u, setU] = useState<Vec>({ x: 4, y: 1 });
  const [v, setV] = useState<Vec>({ x: 2, y: 3 });
  const [goal, setGoal] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const drag = useDrag<Tip>({
    svgRef,
    frame,
    ids: ['u', 'v'],
    pointOf: (t) => (t === 'u' ? u : v),
    onDrag: (t, p) => {
      const q = snapPoint(p, VP, 1, 0.5);
      if (q.x === 0 && q.y === 0) return; // pas de vecteur nul
      if (t === 'u') setU(q);
      else setV(q);
    },
  });

  const d = dot(u, v);
  const s = sign(u, v);
  const theta = angleDeg(u, v);
  const { h, oh } = projection(u, v);
  const nu = exactSqrt(norm2(u));
  const nv = exactSqrt(norm2(v));
  const O = { x: frame.tx(0), y: frame.ty(0) };
  const pu = { x: frame.tx(u.x), y: frame.ty(u.y) };
  const pv = { x: frame.tx(v.x), y: frame.ty(v.y) };
  const ph = { x: frame.tx(h.x), y: frame.ty(h.y) };
  const goalDone = goal !== null && GOALS[goal]!.check(u, v);

  const collinear = u.x * v.y - u.y * v.x === 0;
  const verdict =
    collinear && s === 'positive'
      ? 'Angle nul : u et v sont colinéaires et de même sens, u · v = ‖u‖ × ‖v‖.'
      : collinear && s === 'negative'
        ? 'Angle plat : u et v sont colinéaires et de sens contraires, u · v = −‖u‖ × ‖v‖.'
        : s === 'zero'
          ? 'u · v = 0 : les vecteurs sont orthogonaux, H est en O.'
          : s === 'positive'
            ? 'u · v > 0 : angle aigu, H est du côté de u.'
            : 'u · v < 0 : angle obtus, H est du côté opposé à u.';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <ToggleChip on={goal !== null} onClick={() => setGoal((g) => (g === null ? 0 : null))}>
          Défi
        </ToggleChip>
      </div>

      {goal !== null && (
        <section
          aria-live="polite"
          className="bg-xp-50 space-y-2 rounded-2xl p-3"
          data-testid="dot-goal"
        >
          <p className="text-ink-900 text-sm font-semibold">
            Défi {goal + 1}/{GOALS.length} · {GOALS[goal]!.text}
          </p>
          {goalDone ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-success-600 text-sm font-bold">Réussi !</p>
              {goal + 1 < GOALS.length && (
                <button
                  type="button"
                  onClick={() => setGoal(goal + 1)}
                  className={cn(PRIMARY_BUTTON, 'ml-auto')}
                >
                  Défi suivant
                </button>
              )}
            </div>
          ) : (
            <p className="text-ink-600 text-sm">Déplace les extrémités de u et de v.</p>
          )}
        </section>
      )}

      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="group"
          aria-label="Vecteurs u et v partant de O, et le projeté de v sur la droite de u"
          {...drag.svgProps}
        >
          <PlotGrid frame={frame} step={1} labels={false} />
          {/* droite portée par u */}
          <line
            x1={frame.tx(-20 * u.x)}
            y1={frame.ty(-20 * u.y)}
            x2={frame.tx(20 * u.x)}
            y2={frame.ty(20 * u.y)}
            stroke={U_COLOR}
            strokeOpacity={0.35}
            strokeWidth={1.5}
            strokeDasharray="6 5"
          />
          {/* perpendiculaire de l'extrémité de v vers H */}
          <line
            x1={pv.x}
            y1={pv.y}
            x2={ph.x}
            y2={ph.y}
            stroke="var(--color-ink-400)"
            strokeWidth={1.5}
            strokeDasharray="4 4"
          />
          {s !== 'zero' && Math.hypot(pv.x - ph.x, pv.y - ph.y) > 12 && (
            <RightAngleMark
              x={ph.x}
              y={ph.y}
              ux={O.x - ph.x}
              uy={O.y - ph.y}
              vx={pv.x - ph.x}
              vy={pv.y - ph.y}
              size={8}
              color="var(--color-ink-400)"
            />
          )}
          {/* OH, coloré selon le signe */}
          <line
            x1={O.x}
            y1={O.y}
            x2={ph.x}
            y2={ph.y}
            stroke={SIGN_COLOR[s]}
            strokeWidth={7}
            strokeLinecap="round"
            strokeOpacity={0.55}
            data-testid="dot-projection"
          />
          <path
            d={angleArcPath(O.x, O.y, pu.x, pu.y, pv.x, pv.y, 26)}
            fill="none"
            stroke="var(--color-ink-500)"
            strokeWidth={1.5}
          />
          <VectorArrow x1={O.x} y1={O.y} x2={pu.x} y2={pu.y} color={U_COLOR} inset={8} />
          <VectorArrow x1={O.x} y1={O.y} x2={pv.x} y2={pv.y} color={V_COLOR} inset={8} />
          {s !== 'zero' && (
            <HaloText
              x={ph.x}
              y={ph.y + 18}
              textAnchor="middle"
              fontSize={14}
              fontWeight={700}
              fill="var(--color-ink-700)"
            >
              H
            </HaloText>
          )}
          <HaloText
            x={O.x - 14}
            y={O.y + 16}
            fontSize={14}
            fontWeight={700}
            fill="var(--color-ink-700)"
          >
            O
          </HaloText>
          <DragHandle
            x={pu.x}
            y={pu.y}
            color={U_COLOR}
            label="u"
            labelDx={14}
            labelDy={-14}
            name={`Extrémité de u (${u.x} ; ${u.y})`}
            active={drag.active === 'u'}
            keyProps={drag.keyProps('u')}
          />
          <DragHandle
            x={pv.x}
            y={pv.y}
            color={V_COLOR}
            label="v"
            labelDx={14}
            labelDy={-14}
            name={`Extrémité de v (${v.x} ; ${v.y})`}
            active={drag.active === 'v'}
            keyProps={drag.keyProps('v')}
          />
        </svg>
      </div>

      <section aria-label="Trois façons de calculer u · v" className="space-y-2">
        <div
          className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2"
          data-testid="dot-coords"
        >
          <div className="flex min-h-11 items-center gap-2">
            <span className="text-ink-600 min-w-0 flex-1 text-sm font-semibold">
              Avec les coordonnées (exact)
            </span>
            {onSend && (
              <SendToSheetButton
                label={formatNumber(d)}
                cost={sendCost}
                onClick={() => onSend({ atoms: d < 0 ? ['−', String(-d)] : [String(d)] })}
              />
            )}
          </div>
          <MathText className="text-ink-900 block overflow-x-auto">
            {`$\\vec u\\begin{pmatrix}${u.x}\\\\${u.y}\\end{pmatrix} \\cdot \\vec v\\begin{pmatrix}${v.x}\\\\${v.y}\\end{pmatrix} = ${texFactor(u.x)} \\times ${texFactor(v.x)} + ${texFactor(u.y)} \\times ${texFactor(v.y)} = ${d}$`}
          </MathText>
        </div>
        <div className="border-ink-100 rounded-2xl border px-3 py-2">
          <p className="text-ink-600 text-sm font-semibold">Avec les normes et l’angle</p>
          <MathText className="text-ink-900 block overflow-x-auto">
            {`$\\|\\vec u\\| \\times \\|\\vec v\\| \\times \\cos\\theta = ${nu.tex} \\times ${nv.tex} \\times \\cos(${tex(theta, 1)}^\\circ) ${
              Number.isInteger(d) && s === 'zero' ? '=' : '\\approx'
            } ${tex(Math.sqrt(norm2(u) * norm2(v)) * Math.cos((theta * Math.PI) / 180))}$`}
          </MathText>
        </div>
        <div className="border-ink-100 rounded-2xl border px-3 py-2">
          <p className="text-ink-600 text-sm font-semibold">
            Avec le projeté H de v sur la droite de u
          </p>
          <MathText className="text-ink-900 block overflow-x-auto">
            {`$\\|\\vec u\\| \\times \\overline{OH} \\approx ${nu.tex} \\times (${tex(oh)}) \\approx ${tex(Math.sqrt(norm2(u)) * oh)}$`}
          </MathText>
        </div>
      </section>

      <Note tone={s === 'negative' ? 'error' : s === 'positive' ? 'success' : 'neutral'}>
        {verdict} θ ≈ {formatNumber(theta, 1)}°.
      </Note>
    </div>
  );
}
